import { firstUpper } from '../../../Utils/ramdaCookbook';
import {
  getDay,
  getMonth,
  weekNo,
  getWeekDayText2,
  diffMin,
  diffMSec,
  decSec,
  incDay,
} from '../../../Utils/dateUtils';
import mergeSort from '../../../Utils/mergeSort';
import { dateToStr, timeToStr } from '../../../Utils/stringUtils';
import { IconType } from '../../Icon/Utils/IconDefinitions';
import { VirtuosoHandle } from 'react-virtuoso';
const MINS_PER_DAY = 24 * 60;

export interface IBooking {
  bookingId?: string;
  startTime: Date;
  endTime: Date;
  startTime2: Date; // Activity or resurs compact.
  endTime2: Date; // Activity or resurs compact.
  aktId: string;
  aktTyp?: string;
  aktBokningssignatur?: string;
  aktStartTime?: Date;
  aktEndTime?: Date;
  aktCaption?: string;
  aktLocation?: string;
  resourceId?: string;
  resourceName?: string | null;
  color?: React.CSSProperties['color'];
  background?: React.CSSProperties['background'];
  backgroundLight?: React.CSSProperties['background'];
  shadowBackground?: React.CSSProperties['background'];
  icon?: IBookingIcon[];
  columnCount?: number;
  columnIndex?: number;
  isPerson?: boolean;
  allDay?: boolean;
  prelBokad?: boolean;
  Bokningsnummer?: string | null;
  Huvudakt?: string | null;
  Resursrubrik?: string | null;
}

export interface IBookingIcon {
  icon: IconType;
  tooltip?: string;
}

export interface IResource {
  id: string;
  name: string;
}

export interface ICalendarSelection {
  selStart: Date;
  selEnd: Date | undefined;
  selResources: string[] | undefined;
}

export interface ICalendarViewProps {
  width: number;
  height: number;
  currentDate: Date;
  dayCount?: number;
  /** Extra bottom spacing to clear a floating menu on touch devices */
  bottomSpacing?: number | string;
  /** Numeric inset to subtract from non-scrolling calendar view heights */
  bottomInsetPx?: number;
  bookings: IBooking[];
  allDayBookings: IBooking[];
  regularBookings: IBooking[];
  resources: IResource[];
  selStart?: Date;
  selEnd?: Date;
  selResources?: string[];
  onBookingClick?: (ref: EventTarget, booking: IBooking) => void;
  onSelectionChange?: (
    selStart: Date | undefined,
    selEnd: Date | undefined,
    selResources: string[] | undefined
  ) => void;
  onSelectionClick?: (
    ref: EventTarget,
    selStart: Date | undefined,
    selEnd: Date | undefined,
    selResources: string[] | undefined
  ) => void;
  listViewRef?: React.RefObject<VirtuosoHandle | null>;
}

export const timeToPos = (hour: number, min: number, height: number) =>
  Math.round((hour * 60 + min) * height) / MINS_PER_DAY;

export const posToMinutes = (
  pos: number,
  height: number,
  roundMinutes: number = 30
) => Math.floor(((pos / height) * MINS_PER_DAY) / roundMinutes) * roundMinutes;

export const weekCaption = (d: Date) => `v.${weekNo(d)}`;

export const dateCaption = (d: Date, shortText: boolean = true) =>
  `${firstUpper(getWeekDayText2(d, shortText))} ${getDay(d)}/${getMonth(d)}`;

export const hourCaption = (hour: number) => hour.toString().padStart(2, '0');

export const bookingTimeCaption = (booking: IBooking) =>
  timeToStr(booking.startTime);

export const bookingTimeCaption2 = (booking: IBooking) => {
  const isSameTime = (a?: Date, b?: Date) => a?.valueOf() === b?.valueOf();
  return isSameTime(booking.startTime2, booking.aktStartTime)
    ? timeToStr(booking.startTime2) // Visar aktivitetstid
    : booking.Resursrubrik !== null
    ? '(' + timeToStr(booking.startTime2) + ')'
    : timeToStr(booking.aktStartTime ?? booking.startTime2); // Visar ställtid
};

export const bookingTimeIntervalCaption = (booking: IBooking) =>
  timeToStr(booking.aktStartTime ?? booking.startTime) +
  ' - ' +
  timeToStr(booking.aktEndTime ?? booking.endTime);

export const timeCompare = (time1: Date | undefined, time2: Date | undefined) =>
  time1 ? (time2 ? -Math.sign(diffMSec(time1, time2)) : 1) : time2 ? -1 : 0;

export const minTime = (time1: Date, time2: Date) =>
  timeCompare(time1, time2) <= 0 ? time1 : time2;

export const maxTime = (time1: Date, time2: Date) =>
  timeCompare(time1, time2) >= 0 ? time1 : time2;

export const sameStrArr = (
  strArr1: string[] | undefined,
  strArr2: string[] | undefined
) => {
  const arr1 = strArr1 ?? [];
  const arr2 = strArr2 ?? [];
  return (
    arr1.length === arr2.length && arr1.every((str, idx) => str === arr2[idx])
  );
};

export const timeRangeOverlap = (
  range1Start: Date,
  range1End: Date,
  range2Start: Date,
  range2End: Date
) =>
  timeCompare(
    minTime(range1Start, range1End),
    maxTime(range2Start, range2End)
  ) <= 0 &&
  timeCompare(
    minTime(range2Start, range2End),
    maxTime(range1Start, range1End)
  ) <= 0;

export const updateSelection = (
  newSelStart: Date | undefined,
  newSelEnd: Date | undefined,
  newSelResources: string[] | undefined,
  selStart: Date | undefined,
  selEnd: Date | undefined,
  selResources: string[] | undefined,
  onSelectionChange: (
    selStart: Date | undefined,
    selEnd: Date | undefined,
    selResources: string[] | undefined
  ) => void
) => {
  if (
    timeCompare(newSelStart, selStart) !== 0 ||
    timeCompare(newSelEnd, selEnd) !== 0 ||
    !sameStrArr(newSelResources, selResources)
  ) {
    onSelectionChange(newSelStart, newSelEnd, newSelResources);
  }
};

export const sameAkt = (booking1: IBooking, booking2: IBooking) =>
  booking1.aktId === booking2.aktId;

export const sameAktAndTime = (booking1: IBooking, booking2: IBooking) =>
  booking1.aktId === booking2.aktId &&
  timeCompare(booking1.startTime, booking2.startTime) === 0 &&
  timeCompare(booking1.endTime, booking2.endTime) === 0;

export const groupBookings = (
  bookings: IBooking[],
  /** A function that returns a string key for grouping or undefined to skip the booking. */
  getKey: (booking: IBooking) => string | undefined = booking =>
    dateToStr(booking.startTime),
  /** A function to determine if two bookings are considered equal */
  bookingsAreEqual: (booking1: IBooking, booking2: IBooking) => boolean = (
    booking1,
    booking2
  ) => booking1.aktId === booking2.aktId
): Record<string, IBooking[]> => {
  return bookings.reduce<Record<string, IBooking[]>>((groups, booking) => {
    const key = getKey(booking);
    if (key === undefined) {
      return groups;
    }
    if (!groups[key]) {
      groups[key] = [];
    }
    if (!groups[key].some(b => bookingsAreEqual(b, booking))) {
      groups[key].push(booking);
    }
    return groups;
  }, {});
};

export const sortBookings = (bookings?: IBooking[]) => {
  if (bookings) {
    return mergeSort<IBooking>(bookings, (booking1, booking2) => {
      let comp = -Math.sign(diffMin(booking1.startTime, booking2.startTime));
      if (comp === 0)
        comp = -Math.sign(diffMin(booking1.endTime, booking2.endTime));
      return comp;
    });
  } else {
    return [];
  }
};

export const calculateColumns = (bookings?: IBooking[]) => {
  let currentColumn: IBooking[] = [];
  let currentColumnEnd: Date | undefined = undefined;
  let currentColumnCount = 1;
  sortBookings(bookings).forEach(booking => {
    if (!currentColumnEnd || currentColumnEnd <= booking.startTime) {
      // New group. Calculate width of prevoius group.
      currentColumn.forEach(grpItem => {
        grpItem.columnCount = currentColumnCount;
      });
      // Start new group
      currentColumn = [booking];
      currentColumnEnd = booking.endTime;
      currentColumnCount = 1;
      booking.columnIndex = 0;
      booking.columnCount = 1;
    } else {
      // In a group. Calculate offset.
      booking.columnIndex = currentColumnCount;
      for (let groupIndex = 0; groupIndex < currentColumnCount; groupIndex++) {
        if (
          !currentColumn.find(
            grpItem =>
              grpItem.endTime > booking.startTime &&
              (grpItem.columnIndex ?? 0) === groupIndex
          )
        ) {
          booking.columnIndex = groupIndex;
          break;
        }
      }
      if (booking.columnIndex >= currentColumnCount) {
        currentColumnCount = booking.columnIndex + 1;
      }

      // Continue and possibly expand group
      currentColumn.push(booking);
      if (booking.endTime > currentColumnEnd) {
        currentColumnEnd = booking.endTime;
      }
    }
  });
  // Calculate width of last group.
  currentColumn.forEach(grpItem => {
    grpItem.columnCount = currentColumnCount;
  });
};

export const prepareViewBookings = (
  bookings: IBooking[],
  viewStartTime: Date,
  viewEndTime: Date,
  /** A function that returns a string key for grouping or undefined to skip the booking. */
  getKey: (booking: IBooking) => string | undefined,
  isTimeView: boolean
) => {
  const filterEndTime = decSec(viewEndTime, 1);

  const expandedBookings: IBooking[] = [];
  sortBookings(bookings).forEach(booking => {
    if (!booking.allDay) {
      // One day use the old filter
      if (
        timeRangeOverlap(
          booking.startTime,
          booking.endTime,
          viewStartTime,
          filterEndTime
        )
      ) {
        expandedBookings.push(booking);
      }
      return;
    }
    // allDay booking: compare to the view. Then each per-day
    // copy is guaranteed to be within the view
    const bookingStartday = new Date(booking.startTime);
    bookingStartday.setHours(0, 0, 0, 0);
    const bookingEndDay = new Date(booking.endTime);
    bookingEndDay.setHours(0, 0, 0, 0);
    const viewEnd = new Date(viewEndTime);
    viewEnd.setHours(0, 0, 0, 0);
    // Extra kontroll
    if (booking.endTime <= viewStartTime || bookingStartday >= viewEnd) {
      return;
    }
    let day = new Date(
      Math.max(bookingStartday.getTime(), viewStartTime.getTime())
    );
    const expansionEnd = new Date(
      Math.min(bookingEndDay.getTime(), viewEnd.getTime())
    );
    let endOfDay = new Date(day);
    day.setHours(0, 0, 0, 0);
    endOfDay.setHours(23, 59, 0, 0);
    while (day <= expansionEnd) {
      expandedBookings.push({
        ...booking,
        startTime: day > bookingStartday ? new Date(day) : booking.startTime,
        startTime2: day > bookingStartday ? new Date(day) : booking.startTime2,
        endTime: day < bookingEndDay ? new Date(endOfDay) : booking.endTime,
        endTime2: day < bookingEndDay ? new Date(endOfDay) : booking.endTime2,
      });
      day = incDay(day, 1);
      endOfDay = incDay(endOfDay, 1);
    }
  });

  const bookingGroups = groupBookings(
    expandedBookings,
    getKey,
    isTimeView ? sameAktAndTime : sameAkt
  );
  if (isTimeView) {
    Object.keys(bookingGroups).forEach(key => {
      calculateColumns(bookingGroups[key]);
    });
  }
  return bookingGroups;
};
